import React, { useState, useCallback } from 'react';
import { ChevronRight, ChevronDown, Download } from 'lucide-react';
import type { AMDrillEntry } from '../../../utils/accountOwnerUtils';
import { fmtUSD, fmtUSDExact, fmtHours, buildExportRows } from '../../../utils/accountOwnerUtils';

interface DrillDownTableProps {
  drillDown: Record<number, AMDrillEntry>;
  sortKey: string;
  sortDir: 'asc' | 'desc';
  onSort: (key: string) => void;
}

// ─── Util helpers ─────────────────────────────────────────────────────────────
function utilClass(pct: number): string {
  if (pct >= 80) return 'text-emerald-600 font-semibold';
  if (pct >= 50) return 'text-amber-600 font-semibold';
  return 'text-red-500 font-semibold';
}

function marginClass(val: number): string {
  return val >= 0 ? 'text-emerald-700 font-semibold' : 'text-red-600 font-semibold';
}

function fmtUtil(billable: number, total: number): { pct: number; label: string } {
  const pct = total > 0 ? Math.min(100, (billable / total) * 100) : 0;
  return { pct, label: `${pct.toFixed(1)}%` };
}

// ─── Export helper ────────────────────────────────────────────────────────────
function downloadCSV(drillDown: Record<number, AMDrillEntry>) {
  const rows = buildExportRows(drillDown);
  if (rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(','),
    ...rows.map(r =>
      headers.map(h => {
        const val = String(r[h] ?? '');
        return val.includes(',') ? `"${val}"` : val;
      }).join(',')
    ),
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `account_owner_portfolio_${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Column header ────────────────────────────────────────────────────────────
interface ThProps {
  label: string;
  sortKey?: string;
  activeSortKey: string;
  sortDir: 'asc' | 'desc';
  onSort: (key: string) => void;
  align?: 'left' | 'right';
  className?: string;
  title?: string;
}

const Th: React.FC<ThProps> = ({ label, sortKey, activeSortKey, sortDir, onSort, align = 'left', className = '', title }) => {
  const isActive = sortKey && activeSortKey === sortKey;
  return (
    <th
      title={title}
      className={`
        px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap
        ${align === 'right' ? 'text-right' : 'text-left'}
        ${sortKey ? 'cursor-pointer select-none hover:text-slate-700' : ''}
        ${className}
      `}
      onClick={() => sortKey && onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {isActive && (
          <span className="text-blue-500">{sortDir === 'asc' ? '↑' : '↓'}</span>
        )}
      </span>
    </th>
  );
};

const thLabelClass =
  'px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap text-left';

// ─── Main table ───────────────────────────────────────────────────────────────
export const DrillDownTable: React.FC<DrillDownTableProps> = ({
  drillDown,
  sortKey,
  sortDir,
  onSort,
}) => {
  const [expandedAMs, setExpandedAMs] = useState<Set<number>>(new Set());
  const [expandedClients, setExpandedClients] = useState<Set<string>>(new Set());
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());

  const toggleAM = useCallback((id: number) => {
    setExpandedAMs(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleClient = useCallback((key: string) => {
    setExpandedClients(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleProject = useCallback((key: string) => {
    setExpandedProjects(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const amList = Object.values(drillDown).sort((a, b) => {
    let av: number, bv: number;
    switch (sortKey) {
      case 'billable': av = a.billable; bv = b.billable; break;
      case 'total': av = a.total; bv = b.total; break;
      case 'revenue': av = a.revenue; bv = b.revenue; break;
      case 'drc': av = a.directResourceCost; bv = b.directResourceCost; break;
      case 'cost': av = a.cost; bv = b.cost; break;
      case 'margin': av = a.profit; bv = b.profit; break;
      case 'utilization': av = a.utilization; bv = b.utilization; break;
      default: av = a.revenue; bv = b.revenue;
    }
    return sortDir === 'asc' ? av - bv : bv - av;
  });

  const isEmpty = amList.length === 0;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden flex flex-col">
      {/* Table title + export */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
          Portfolio Drill-Down
        </p>
        <button
          onClick={() => downloadCSV(drillDown)}
          disabled={isEmpty}
          className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          title="Export to CSV"
        >
          <Download size={13} />
          Export
        </button>
      </div>

      {/* Scrollable table — full-width card, generous height on large screens */}
      <div className="overflow-auto max-h-[min(75vh,56rem)]">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-slate-100 border-b-2 border-slate-200">
            <tr>
              <th
                className="w-10 min-w-[2.25rem] max-w-[2.25rem] px-1 py-2.5"
                aria-label="Expand"
              />
              <Th label="Name" activeSortKey={sortKey} sortDir={sortDir} onSort={onSort} className="min-w-[11rem] w-[22%]" />
              <th className={`${thLabelClass} min-w-[8rem] max-w-[14rem]`}>Client</th>
              <th className={`${thLabelClass} min-w-[7rem] max-w-[12rem]`}>PM</th>
              <Th label="Billable hrs" sortKey="billable" activeSortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />
              <Th label="Total hrs" sortKey="total" activeSortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />
              <Th label="Util %" sortKey="utilization" activeSortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />
              <Th label="Revenue" sortKey="revenue" activeSortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />
              <Th
                label="Cost"
                sortKey="cost"
                activeSortKey={sortKey}
                sortDir={sortDir}
                onSort={onSort}
                align="right"
                className="cursor-pointer"
                title="Billable hrs × raw grade × tech rate (list rate, before misc.)"
              />
              <th
                className="px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap text-right cursor-pointer select-none hover:text-slate-700"
                onClick={() => onSort('drc')}
                title="Direct resource cost: billable hrs × effective rate after misc. ((raw − misc) × hrs)"
              >
                <span className="inline-flex items-center gap-1">
                  DRC
                  {sortKey === 'drc' && (
                    <span className="text-blue-500">{sortDir === 'asc' ? '↑' : '↓'}</span>
                  )}
                </span>
              </th>
              <Th label="Margin" sortKey="margin" activeSortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />
            </tr>
          </thead>
          <tbody>
            {isEmpty ? (
              <tr className="bg-white">
                <td colSpan={11} className="px-4 py-12 text-center text-slate-600 text-sm border-t border-slate-100">
                  No data matches the selected filters.
                </td>
              </tr>
            ) : (
              amList.map(am => {
                const amOpen = expandedAMs.has(am.amId);
                const amUtil = fmtUtil(am.billable, am.total);

                const clientList = Object.values(am.clients).sort((a, b) =>
                  (a.clientName || '').localeCompare(b.clientName || '', undefined, {
                    sensitivity: 'base',
                  })
                );

                return (
                  <React.Fragment key={am.amId}>
                    {/* ── AM row ──────────────────────────────────────── */}
                    <tr
                      className="cursor-pointer select-none bg-white border-t border-slate-100 border-l-[3px] border-l-blue-500 hover:bg-blue-50/60 transition-colors text-sm"
                      onClick={() => toggleAM(am.amId)}
                    >
                      <td className="w-10 min-w-[2.25rem] max-w-[2.25rem] px-1 py-2.5 align-middle text-slate-500">
                        <span className="inline-flex" aria-hidden>
                          {amOpen ? <ChevronDown size={14} strokeWidth={2} /> : <ChevronRight size={14} strokeWidth={2} />}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 align-middle min-w-0 max-w-[min(20rem,36vw)]">
                        <span className="block truncate font-semibold text-slate-900" title={am.amName}>
                          {am.amName}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 align-middle text-sm text-slate-400">—</td>
                      <td className="px-3 py-2.5 align-middle text-sm text-slate-400">—</td>
                      <td className="px-3 py-2.5 align-middle text-right tabular-nums text-slate-800">{fmtHours(am.billable)}</td>
                      <td className="px-3 py-2.5 align-middle text-right tabular-nums text-slate-800">{fmtHours(am.total)}</td>
                      <td className={`px-3 py-2.5 align-middle text-right tabular-nums text-sm ${utilClass(amUtil.pct)}`}>
                        {amUtil.label}
                      </td>
                      <td className="px-3 py-2.5 align-middle text-right tabular-nums font-semibold text-slate-900" title={fmtUSDExact(am.revenue)}>{fmtUSD(am.revenue)}</td>
                      <td className="px-3 py-2.5 align-middle text-right tabular-nums text-slate-600" title={fmtUSDExact(am.cost)}>{fmtUSD(am.cost)}</td>
                      <td className="px-3 py-2.5 align-middle text-right tabular-nums text-slate-700" title={fmtUSDExact(am.directResourceCost)}>{fmtUSD(am.directResourceCost)}</td>
                      <td className={`px-3 py-2.5 align-middle text-right tabular-nums text-sm ${marginClass(am.profit)}`} title={fmtUSDExact(am.profit)}>
                        {fmtUSD(am.profit)}
                      </td>
                    </tr>

                    {/* ── Client rows ──────────────────────────────────── */}
                    {amOpen && clientList.map(cl => {
                      const clientRowKey = `${am.amId}:${cl.clientKey}`;
                      const clOpen = expandedClients.has(clientRowKey);
                      const clUtil = fmtUtil(cl.billable, cl.total);
                      const projList = Object.values(cl.projects).sort((a, b) =>
                        (a.projectName || '').localeCompare(b.projectName || '', undefined, {
                          sensitivity: 'base',
                        })
                      );

                      return (
                        <React.Fragment key={clientRowKey}>
                          <tr
                            className="cursor-pointer bg-slate-50/70 border-t border-slate-100 hover:bg-blue-50/60 transition-colors text-sm"
                            onClick={() => toggleClient(clientRowKey)}
                          >
                            <td className="w-10 min-w-[2.25rem] max-w-[2.25rem] px-1 py-2.5 align-middle text-slate-400">
                              <span className="inline-flex" aria-hidden>
                                {clOpen ? <ChevronDown size={14} strokeWidth={2} /> : <ChevronRight size={14} strokeWidth={2} />}
                              </span>
                            </td>
                            <td className="pl-2 pr-3 py-2.5 align-middle min-w-0 max-w-[min(20rem,36vw)]">
                              <span className="block truncate font-medium text-slate-800" title={cl.clientName || undefined}>
                                {cl.clientName || '—'}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 align-middle text-sm text-slate-400">—</td>
                            <td className="px-3 py-2.5 align-middle text-sm text-slate-400">—</td>
                            <td className="px-3 py-2.5 align-middle text-right tabular-nums text-slate-600">{fmtHours(cl.billable)}</td>
                            <td className="px-3 py-2.5 align-middle text-right tabular-nums text-slate-600">{fmtHours(cl.total)}</td>
                            <td className={`px-3 py-2.5 align-middle text-right tabular-nums text-sm ${utilClass(clUtil.pct)}`}>{clUtil.label}</td>
                            <td className="px-3 py-2.5 align-middle text-right tabular-nums text-slate-800 font-medium" title={fmtUSDExact(cl.revenue)}>{fmtUSD(cl.revenue)}</td>
                            <td className="px-3 py-2.5 align-middle text-right tabular-nums text-slate-600" title={fmtUSDExact(cl.cost)}>{fmtUSD(cl.cost)}</td>
                            <td className="px-3 py-2.5 align-middle text-right tabular-nums text-slate-700" title={fmtUSDExact(cl.directResourceCost)}>{fmtUSD(cl.directResourceCost)}</td>
                            <td className={`px-3 py-2.5 align-middle text-right tabular-nums text-sm ${marginClass(cl.profit)}`} title={fmtUSDExact(cl.profit)}>{fmtUSD(cl.profit)}</td>
                          </tr>

                          {/* ── Project rows ────────────────────────── */}
                          {clOpen && projList.map(proj => {
                            const projRowKey = `${clientRowKey}:${proj.projectId}`;
                            const projOpen = expandedProjects.has(projRowKey);
                            const projUtil = fmtUtil(proj.billable, proj.total);
                            const resList = Object.values(proj.resources).sort((a, b) =>
                              (a.resourceName || '').localeCompare(b.resourceName || '', undefined, {
                                sensitivity: 'base',
                              })
                            );

                            return (
                              <React.Fragment key={projRowKey}>
                                <tr
                                  className="cursor-pointer bg-white border-t border-slate-100 hover:bg-blue-50/60 transition-colors text-sm"
                                  onClick={() => toggleProject(projRowKey)}
                                >
                                  <td className="w-10 min-w-[2.25rem] max-w-[2.25rem] px-1 py-2.5 align-middle text-slate-400">
                                    <span className="inline-flex" aria-hidden>
                                      {projOpen ? <ChevronDown size={14} strokeWidth={2} /> : <ChevronRight size={14} strokeWidth={2} />}
                                    </span>
                                  </td>
                                  <td className="pl-4 pr-3 py-2.5 align-middle min-w-0 max-w-[min(20rem,36vw)]">
                                    <span className="block truncate font-medium text-slate-800" title={proj.projectName}>
                                      {proj.projectName}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2.5 align-middle min-w-0 max-w-[14rem]">
                                    <span className="block truncate text-sm text-slate-700" title={cl.clientName || undefined}>
                                      {cl.clientName || '—'}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2.5 align-middle min-w-0 max-w-[12rem]">
                                    <span className="block truncate text-sm text-slate-800" title={proj.projectManager}>
                                      {proj.projectManager}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2.5 align-middle text-right tabular-nums text-slate-600">{fmtHours(proj.billable)}</td>
                                  <td className="px-3 py-2.5 align-middle text-right tabular-nums text-slate-600">{fmtHours(proj.total)}</td>
                                  <td className={`px-3 py-2.5 align-middle text-right tabular-nums text-sm ${utilClass(projUtil.pct)}`}>{projUtil.label}</td>
                                  <td className="px-3 py-2.5 align-middle text-right tabular-nums text-slate-800" title={fmtUSDExact(proj.revenue)}>{fmtUSD(proj.revenue)}</td>
                                  <td className="px-3 py-2.5 align-middle text-right tabular-nums text-slate-600" title={fmtUSDExact(proj.cost)}>{fmtUSD(proj.cost)}</td>
                                  <td className="px-3 py-2.5 align-middle text-right tabular-nums text-slate-700" title={fmtUSDExact(proj.directResourceCost)}>{fmtUSD(proj.directResourceCost)}</td>
                                  <td className={`px-3 py-2.5 align-middle text-right tabular-nums text-sm ${marginClass(proj.profit)}`} title={fmtUSDExact(proj.profit)}>{fmtUSD(proj.profit)}</td>
                                </tr>

                                {/* ── Resource rows ──────────────── */}
                                {projOpen && resList.map(res => {
                                  const resUtil = fmtUtil(res.billable, res.total);
                                  return (
                                    <tr
                                      key={res.resourceId}
                                      className="bg-slate-50/70 border-t border-slate-100 text-sm"
                                    >
                                      <td className="w-10 min-w-[2.25rem] max-w-[2.25rem] px-1 py-2.5 align-middle" />
                                      <td className="pl-6 pr-3 py-2.5 align-middle min-w-0 max-w-[min(20rem,36vw)]">
                                        <span className="block truncate text-slate-700 font-medium" title={res.resourceName}>
                                          {res.resourceName}
                                        </span>
                                      </td>
                                      <td className="px-3 py-2.5 align-middle min-w-0 max-w-[14rem]" />
                                      <td className="px-3 py-2.5 align-middle text-sm text-slate-400">—</td>
                                      <td className="px-3 py-2.5 align-middle text-right tabular-nums text-slate-600">{fmtHours(res.billable)}</td>
                                      <td className="px-3 py-2.5 align-middle text-right tabular-nums text-slate-600">{fmtHours(res.total)}</td>
                                      <td className={`px-3 py-2.5 align-middle text-right tabular-nums text-sm ${utilClass(resUtil.pct)}`}>{resUtil.label}</td>
                                      <td className="px-3 py-2.5 align-middle text-right tabular-nums text-slate-800" title={fmtUSDExact(res.revenue)}>{fmtUSD(res.revenue)}</td>
                                      <td className="px-3 py-2.5 align-middle text-right tabular-nums text-slate-600" title={fmtUSDExact(res.cost)}>{fmtUSD(res.cost)}</td>
                                      <td className="px-3 py-2.5 align-middle text-right tabular-nums text-slate-700" title={fmtUSDExact(res.directResourceCost)}>{fmtUSD(res.directResourceCost)}</td>
                                      <td className={`px-3 py-2.5 align-middle text-right tabular-nums text-sm ${marginClass(res.profit)}`} title={fmtUSDExact(res.profit)}>{fmtUSD(res.profit)}</td>
                                    </tr>
                                  );
                                })}
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DrillDownTable;
